/*
 * cv-analytics.js — Car Verif deep funnel analytics + error monitoring.
 *
 * Three jobs, all fail-safe (everything wrapped in try/catch, loaded deferred):
 *   1. Sentry Browser (CDN bundle, lazy-loaded async) for JS error monitoring.
 *   2. Deep funnel events to the existing first-party Cloudflare beacon
 *      (carverif-wheel-beacon.walid-pro.workers.dev) — reuses the SAME schema
 *      as snippets/cv-v2-wheel.liquid: navigator.sendBeacon JSON {event, ...props}.
 *   3. (CF Web Analytics beacon is added separately in layout <head>.)
 *
 * This file MUST NEVER break the page. Any failure is swallowed silently.
 * It deliberately does NOT re-fire add_to_cart / wheel events — those already
 * live in cv-v2-wheel.liquid and the cart/product sections.
 */
(function () {
  "use strict";
  try {
    // Never run in the theme editor / preview design mode.
    if (window.Shopify && window.Shopify.designMode) return;
    // Idempotent: never double-init if injected twice.
    if (window.__cvAnalytics) return;
    window.__cvAnalytics = true;

    var BEACON = "https://carverif-wheel-beacon.walid-pro.workers.dev/e";
    var SENTRY_DSN =
      "https://ff0faa1e3a6f502bb8d781ea28f93c92@o4511365957287936.ingest.de.sentry.io/4511581088448592";
    var SENTRY_VER = "8.42.0"; // pinned Sentry Browser CDN bundle version

    /* ---------------------------------------------------------------------- *
     * 1. First-party beacon — identical contract to cv-v2-wheel.liquid track()
     * ---------------------------------------------------------------------- */
    function track(name, props) {
      try {
        var payload = { event: name, path: location.pathname, ts: Date.now() };
        if (props) for (var k in props) if (props.hasOwnProperty(k)) payload[k] = props[k];
        var body = JSON.stringify(payload);
        if (navigator.sendBeacon) {
          navigator.sendBeacon(BEACON, body);
        } else {
          fetch(BEACON, { method: "POST", body: body, keepalive: true, mode: "no-cors" });
        }
        // mirror to dataLayer / gtag if present (same as the wheel does)
        try { window.dataLayer = window.dataLayer || []; window.dataLayer.push(payload); } catch (e) {}
        try { if (typeof window.gtag === "function") window.gtag("event", name, props || {}); } catch (e) {}
      } catch (e) {}
    }

    /* ---------------------------------------------------------------------- *
     * 2. Sentry Browser — lazy CDN load, init on load, fully non-blocking
     * ---------------------------------------------------------------------- */
    function initSentry() {
      try {
        // Only monitor evergreen browsers. Ancient browsers (Chrome <76, etc.)
        // can't parse modern JS bundles (Shopify trekkie, app scripts), producing
        // un-actionable SyntaxError / "X is undefined" noise that is not our code.
        if (!(window.Promise && window.fetch && Promise.allSettled)) return;
        var s = document.createElement("script");
        s.src = "https://browser.sentry-cdn.com/" + SENTRY_VER + "/bundle.tracing.min.js";
        s.crossOrigin = "anonymous";
        s.async = true;
        s.onload = function () {
          try {
            if (!window.Sentry || !window.Sentry.init) return;
            window.Sentry.init({
              dsn: SENTRY_DSN,
              environment: "production",
              release: "carverif-theme-sentry-20261026",
              sampleRate: 1.0,          // capture 100% of errors
              tracesSampleRate: 0.1,    // 10% performance traces
              autoSessionTracking: true,
              // Drop third-party / infra noise so real first-party errors stand out.
              ignoreErrors: [
                "Non-Error promise rejection captured",
                "UET is not defined",
                "importScripts",          // Shopify Web Pixels sandbox load failures
                "Importing a module script failed",          // dynamic import() blocked/transient (STOREFRONT-B)
                "Failed to fetch dynamically imported module",
                "error loading dynamically imported module",
                "NetworkError when attempting to fetch resource",
                "Load failed",            // Safari transient fetch/network drop (STOREFRONT-9)
                "window.webkit.messageHandlers",   // Instagram iOS in-app browser bridge (STOREFRONT-S)
                "Java object is gone",             // Facebook Android in-app browser bridge (STOREFRONT-X)
                "Invalid call to runtime.sendMessage" // browser-extension messaging (STOREFRONT-T)
              ],
              denyUrls: [
                /cdn\/wpm\//i,            // Shopify Web Pixel Manager
                /web-pixels/i,            // Shopify Web Pixels sandbox worker (web-pixels@… has no slash)
                /trekkie/i,               // Shopify trekkie analytics (third-party)
                /shopifycloud/i,          // Shopify storefront infra (shop_events_listener…)
                /bat\.bing\.com/i,        // Bing UET (blocked/partial loads)
                /beacon\.min\.js/i,       // Cloudflare Web Analytics beacon — uses Array.prototype.at, crashes Chrome <92 (STOREFRONT-Y)
                /^iabjs:/i,               // Facebook in-app browser injected logger (STOREFRONT-X)
                /\/__av\//i,              // antivirus-proxied script rewrites (STOREFRONT-W/V)
                /^chrome-extension:\/\//i,
                /^moz-extension:\/\//i,
                /^safari-extension:\/\//i
              ],
              // In-app browsers (Facebook/Instagram/Huawei WebView) inject scripts that
              // throw SyntaxErrors attributed to the HTML document — their only stack frame
              // is the page URL, never a real .js file. Drop those; real first-party errors
              // always carry a .js frame. (STOREFRONT-C / STOREFRONT-A)
              beforeSend: function (event) {
                try {
                  var ex = (event.exception && event.exception.values && event.exception.values[0]) || {};
                  var frames = (ex.stacktrace && ex.stacktrace.frames) || [];
                  // denyUrls only checks the last frame. Shopify's requests can
                  // end in a Sentry/browser/inline wrapper instead (STOREFRONT-E/10).
                  // Keep any event involving a controlled theme asset, even if
                  // it also calls a vendor, and only exclude confirmed signatures.
                  var hasThemeFrame = frames.some(function (f) {
                    return f && /(?:\/cdn\/shop\/t\/\d+\/assets\/|^(?:https?:\/\/(?:www\.)?carverif\.fr)?\/assets\/)/i.test(f.filename || "");
                  });
                  var value = ex.value || "";
                  function hasFrame(pattern) {
                    return frames.some(function (f) { return f && pattern.test(f.filename || ""); });
                  }
                  if (!hasThemeFrame) {
                    // Brave iOS WalletTabHelper injects these assignments into
                    // every page; Car Verif has no Ethereum integration (1F).
                    if (ex.type === "TypeError" && /window\.ethereum\.(selectedAddress|chainId|networkVersion) = undefined/.test(value)) return null;
                    if (ex.type === "SecurityError" && /sendBeacon.*Blob.*CORS-safelisted/.test(value) && hasFrame(/\/cdn\/s\/trekkie\./i)) return null;
                    if (ex.type === "TypeError" && value === "Failed to fetch" && hasFrame(/\/shopify_pay\/storefront[^/]*\.js/i)) return null;
                    // Shopify standard-actions uses modern syntax unsupported
                    // by Chrome 80 WebViews (17), outside our theme bundle.
                    if (ex.type === "SyntaxError" && hasFrame(/\/storefront\/standard-actions\.js(?:[?#]|$)/i)) return null;
                  }
                  if ((ex.type || "") !== "SyntaxError") return event;
                  var hasScriptFrame = frames.some(function (f) {
                    return f && f.filename && /\.m?js(\?|#|$)/i.test(f.filename);
                  });
                  if (!hasScriptFrame) return null;
                } catch (e) {}
                return event;
              }
            });
          } catch (e) {}
        };
        s.onerror = function () {}; // CDN down → no-op, page unaffected
        (document.head || document.documentElement).appendChild(s);
      } catch (e) {}
    }

    /* ---------------------------------------------------------------------- *
     * 3. Deep funnel events
     * ---------------------------------------------------------------------- */
    var path = location.pathname;

    // -- view_product ---------------------------------------------------------
    function fireViewProduct() {
      try {
        if (path.indexOf("/products/") === 0) {
          var props = { src: "product_page" };
          var meta = document.querySelector('meta[property="product:price:amount"]');
          if (meta && meta.content) props.value = meta.content;
          track("view_product", props);
        }
      } catch (e) {}
    }

    // -- begin_checkout (path-based) ------------------------------------------
    function fireBeginCheckoutByPath() {
      try {
        if (path.indexOf("/checkout") === 0 || path.indexOf("/checkouts/") === 0) {
          track("begin_checkout", { src: "checkout_page" });
        }
      } catch (e) {}
    }

    // -- purchase (best-effort thank-you / order-status detection) ------------
    // Shopify's hosted checkout/thank-you page normally renders OUTSIDE the
    // theme, so this only fires on order-status pages that DO load theme JS.
    // Harmless no-op otherwise. See report note.
    function firePurchase() {
      try {
        var p = path.toLowerCase();
        var isThankYou =
          /\/thank[_-]?you/.test(p) ||
          p.indexOf("/orders/") === 0 ||
          /order[_-]?status/.test(p) ||
          (window.Shopify && window.Shopify.Checkout && window.Shopify.Checkout.page === "thank_you");
        if (!isThankYou) return;
        var props = { src: "order_status" };
        try {
          if (window.Shopify && window.Shopify.checkout) {
            var c = window.Shopify.checkout;
            if (c.order_id) props.order_id = String(c.order_id);
            if (c.total_price) props.value = String(c.total_price);
            if (c.currency) props.currency = c.currency;
          }
        } catch (e) {}
        track("purchase", props);
      } catch (e) {}
    }

    // -- plate_input (focus once + submit) -----------------------------------
    var PLATE_RX = /(plate|plaque|immat|registration)/i;
    function isPlateField(el) {
      try {
        if (!el || el.tagName !== "INPUT") return false;
        if (el.id === "cv-v2-cta-bar-input") return true;
        var hay = (el.id || "") + " " + (el.name || "") + " " +
                  (el.getAttribute("placeholder") || "") + " " +
                  (el.getAttribute("aria-label") || "") + " " + (el.className || "");
        return PLATE_RX.test(hay);
      } catch (e) { return false; }
    }
    var plateFocused = false;
    document.addEventListener("focusin", function (e) {
      try {
        if (!plateFocused && isPlateField(e.target)) {
          plateFocused = true;
          track("plate_input", { stage: "focus", src: srcForElement(e.target) });
        }
      } catch (err) {}
    }, true);
    document.addEventListener("submit", function (e) {
      try {
        var form = e.target;
        if (!form || !form.querySelector) return;
        var plate = null, inputs = form.querySelectorAll("input"), i;
        for (i = 0; i < inputs.length; i++) { if (isPlateField(inputs[i])) { plate = inputs[i]; break; } }
        if (plate) track("plate_input", { stage: "submit", src: srcForElement(form) });
      } catch (err) {}
    }, true);

    // -- cta_click (sticky bar, buy buttons, checkout links) ------------------
    function srcForElement(el) {
      try {
        if (!el) return "unknown";
        if (el.closest && el.closest(".cv-v2-cta-bar")) return "sticky_cta_bar";
        if (el.closest && el.closest(".cd-cta-sticky")) return "obd_sticky_cta";
        if (el.closest && el.closest(".conseils-sticky-cta")) return "conseils_sticky_cta";
        if (el.closest && el.closest("form[action*='/cart']")) return "product_form";
        return "page";
      } catch (e) { return "unknown"; }
    }
    document.addEventListener("click", function (e) {
      try {
        var el = e.target;
        if (!el || !el.closest) return;
        var btn = el.closest("button, a, input[type=submit]");
        if (!btn) return;

        // primary add-to-cart / buy
        if (btn.matches && (btn.matches("[name='add']") || btn.closest(".shopify-payment-button"))) {
          track("cta_click", { cta: "buy", src: srcForElement(btn) });
          return;
        }
        // checkout intent → begin_checkout (covers cart "checkout" button + links)
        var href = (btn.getAttribute && btn.getAttribute("href")) || "";
        if (
          (btn.name === "checkout") ||
          /\/checkout(s)?(\/|$|\?)/.test(href) ||
          (btn.matches && btn.matches("[name='checkout'], .cart__checkout-button, #checkout"))
        ) {
          track("cta_click", { cta: "checkout", src: srcForElement(btn) });
          track("begin_checkout", { src: "cta_click" });
          return;
        }
        // sticky CTA bar submit button
        if (btn.closest && btn.closest(".cv-v2-cta-bar")) {
          track("cta_click", { cta: "sticky_bar", src: "sticky_cta_bar" });
          return;
        }
        // generic primary CTA (class hints), kept conservative
        var cls = (btn.className && btn.className.baseVal !== undefined)
          ? btn.className.baseVal : (btn.className || "");
        if (/\b(cta|buy|btn--primary|button--primary)\b/i.test(cls) && srcForElement(btn) !== "page") {
          track("cta_click", { cta: "primary", src: srcForElement(btn) });
        }
      } catch (err) {}
    }, true);

    // -- scroll_depth (25/50/75/100) -----------------------------------------
    (function scrollDepth() {
      try {
        var marks = [25, 50, 75, 100], fired = {}, ticking = false;
        function check() {
          ticking = false;
          try {
            var doc = document.documentElement, body = document.body;
            var sh = Math.max(doc.scrollHeight, body ? body.scrollHeight : 0);
            var st = window.scrollY || doc.scrollTop || 0;
            var vh = window.innerHeight || doc.clientHeight;
            if (sh <= vh) return; // page not scrollable
            var pct = ((st + vh) / sh) * 100;
            for (var i = 0; i < marks.length; i++) {
              var m = marks[i];
              if (!fired[m] && pct >= m) {
                fired[m] = true;
                track("scroll_depth", { depth: m });
              }
            }
          } catch (e) {}
        }
        window.addEventListener("scroll", function () {
          if (!ticking) { ticking = true; (window.requestAnimationFrame || setTimeout)(check); }
        }, { passive: true });
      } catch (e) {}
    })();

    /* ---------------------------------------------------------------------- *
     * Boot — defer non-critical work off the critical path
     * ---------------------------------------------------------------------- */
    function boot() {
      fireViewProduct();
      fireBeginCheckoutByPath();
      firePurchase();
    }
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", boot);
    } else {
      boot();
    }
    // Sentry + page_view fire after load, never competing with LCP.
    var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 1200); };
    idle(function () {
      try { track("page_view", { src: "theme" }); } catch (e) {}
      initSentry();
    });
  } catch (e) { /* analytics must never break the page */ }
})();

